import { useQuery } from '@tanstack/react-query'
import { lazy, Suspense, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { Button } from '@/components/base/Button'
import { ButtonRadio } from '@/components/base/ButtonRadio'
import { Divider } from '@/components/base/Divider'
import { Dropdown } from '@/components/base/Dropdown'
import { Loading } from '@/components/base/Loading'
import { Pagination } from '@/components/base/Pagination'
import { CollapseTableRow } from '@/components/CollapseTableRow'
import { EmptyView } from '@/components/EmptyView'
import { Trans } from '@/components/Trans'
import { getEIPListPath, useUnifiedContext } from '@/contexts/UnifiedContext'
import { useNATListQuery } from '@/hooks/useNATListQuery'
import { PurchasedEIP } from '@/models/eip'
import { formatBitsPerSec, formatDomain, formatPrice } from '@/utils/formatter'

import { EIPBreadcrumb } from './components/EIPBreadcrumb'
import { EIPDetail } from './components/EIPDetail'

const EIPCreateModal = lazy(() =>
  import('./components/EIPCreateModal').then((m) => ({ default: m.EIPCreateModal })),
)
const EIPDeleteModal = lazy(() =>
  import('./components/EIPDeleteModal').then((m) => ({ default: m.EIPDeleteModal })),
)
const EIPEditBandwidthModal = lazy(() =>
  import('./components/EIPEditBandwidthModal').then((m) => ({ default: m.EIPEditBandwidthModal })),
)

const REGION_GLOBAL = -1
const REGION_DATA_DRIVE = -2
const REGION_MESH_CLUSTERING = -3
const EIP_NETWORK_TYPE = 6 // NAT4 - Elastic IP

type AccessPointType = 'publicIP'

export function EIPListPage() {
  const { t } = useTranslation('eip')
  const { t: tCommon } = useTranslation()
  const ctx = useUnifiedContext()
  const eipListPath = getEIPListPath(ctx)
  const [page, setPage] = useState(1)
  const pageSize = 10
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [deleteEIP, setDeleteEIP] = useState<PurchasedEIP | undefined>()
  const [editEIP, setEditEIP] = useState<PurchasedEIP | undefined>()
  const [regionFilterCriteria, setRegionFilterCriteria] = useState<number>(REGION_GLOBAL)
  const [accessPointType, setAccessPointType] = useState<AccessPointType>('publicIP')

  const { data: domainInfos = [], isLoading: domainLoading } = useQuery({
    queryKey: ['domainList', ctx.mode, ctx.teamResID, ctx.memberResID],
    queryFn: async ({ signal }) => {
      const res = await API.getDomainList(undefined, { signal })
      if (res.code !== 0) throw new Error(res.msg)
      return res.domainInfos ?? []
    },
  })

  const natQueryParams = useMemo(() => {
    let apiRegionFilterCriteria = 0
    if (regionFilterCriteria === REGION_DATA_DRIVE) apiRegionFilterCriteria = 1
    else if (regionFilterCriteria === REGION_MESH_CLUSTERING) apiRegionFilterCriteria = 2

    const isSpecialFilter = [REGION_GLOBAL, REGION_DATA_DRIVE, REGION_MESH_CLUSTERING].includes(
      regionFilterCriteria,
    )
    const regions = isSpecialFilter ? domainInfos.map((i) => i.domain) : [regionFilterCriteria]

    return {
      page,
      per_page: pageSize,
      network_type: EIP_NETWORK_TYPE,
      regions,
      region_filter_criteria: apiRegionFilterCriteria,
    }
  }, [page, regionFilterCriteria, domainInfos])

  const {
    networks,
    pagination,
    isLoading: natLoading,
    refetch: refetchNATs,
  } = useNATListQuery({
    params: natQueryParams,
    enabled: domainInfos.length > 0,
  })

  const eips = useMemo(() => networks.map((data) => new PurchasedEIP(data)), [networks])
  const total = pagination?.total ?? 0

  const stockRegionIds = useMemo(() => domainInfos.map((info) => info.domain), [domainInfos])

  const {
    data: isStockDepleted = false,
    isLoading: stockLoading,
    isFetching: stockFetching,
    refetch: refetchStock,
  } = useQuery({
    queryKey: ['eipStockGlobal', ctx.mode, ctx.teamResID, ctx.memberResID, stockRegionIds],
    queryFn: async ({ signal }) => {
      const results = await Promise.all(
        stockRegionIds.map(async (region) => {
          const res = await API.getAvailableEIPs(
            { page: 1, per_page: 1, region, region_filter_criteria: 0 },
            { signal },
          )

          if (res.code !== 0) {
            return { checked: false, hasStock: false }
          }

          return { checked: true, hasStock: (res.eIPS?.length ?? 0) > 0 }
        }),
      )

      if (results.some((result) => result.hasStock)) return false
      return results.every((result) => result.checked)
    },
    enabled: stockRegionIds.length > 0,
  })

  const getRegionDisplayName = useCallback(
    (domain: number): string => {
      const found = domainInfos.find((info) => info.domain === domain)
      return found?.displayName || formatDomain(domain)
    },
    [domainInfos],
  )

  const dropdownOptions = useMemo(
    () => [
      { label: tCommon('common.region.groupGlobal'), value: REGION_GLOBAL, className: 't-sm' },
      {
        label: tCommon('common.region.groupDataDrive'),
        value: REGION_DATA_DRIVE,
        className: 't-sm',
      },
      {
        label: tCommon('common.region.groupMeshClustering'),
        value: REGION_MESH_CLUSTERING,
        className: 't-sm',
      },
      ...domainInfos.map((info, index) => ({
        label: info.displayName || formatDomain(info.domain),
        value: info.domain,
        className: 't-sm',
        separatorBefore: index === 0,
      })),
    ],
    [domainInfos, tCommon],
  )

  return (
    <div className="card bg-panel-white h-full overflow-y-auto">
      <div className="flex items-start justify-between mb-4">
        <EIPBreadcrumb
          items={[
            {
              label: (
                <Trans ns="eip" i18nKey="pageTitle">
                  Access Point
                </Trans>
              ),
              to: eipListPath,
              active: true,
            },
          ]}
        />
        <Button
          type="secondary"
          onClick={() => {
            setShowCreateModal(true)
          }}
        >
          <Trans ns="eip" i18nKey="createBtn">
            Create New
          </Trans>
        </Button>
      </div>
      <Divider />
      <div className="flex justify-start items-center gap-8">
        <ButtonRadio
          value={accessPointType}
          items={[{ label: t('tab.publicIP'), value: 'publicIP' as AccessPointType }]}
          onChange={setAccessPointType}
        />
        <Dropdown
          rootClassName="t-sb w-60"
          className="h-11"
          options={dropdownOptions}
          value={regionFilterCriteria}
          onChange={(value) => {
            setRegionFilterCriteria(Number(value))
            setPage(1)
          }}
        />
      </div>
      {domainLoading || natLoading || (eips.length === 0 && (stockLoading || stockFetching)) ? (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      ) : eips.length > 0 ? (
        <table className="table-card mt-4 table-fixed">
          <thead>
            <tr>
              <th colSpan={3}>
                <Trans ns="eip" i18nKey="tableHeaderID">
                  ID
                </Trans>
              </th>
              <th colSpan={5}>
                <Trans ns="eip" i18nKey="tableHeaderIPAddress">
                  Address
                </Trans>
              </th>
              <th colSpan={3}>
                <Trans ns="eip" i18nKey="tableHeaderRegion">
                  Region
                </Trans>
              </th>
              <th colSpan={3}>
                <Trans ns="eip" i18nKey="tableHeaderBandwidth">
                  Bandwidth
                </Trans>
              </th>
              <th colSpan={3}>
                <Trans ns="eip" i18nKey="tableHeaderStatus">
                  Status
                </Trans>
              </th>
              <th colSpan={3}>
                <div className="text-right">
                  <Trans ns="eip" i18nKey="tableHeaderCost">
                    Cost
                  </Trans>
                </div>
              </th>
              <th colSpan={3}>
                <Trans ns="eip" i18nKey="tableHeaderAction">
                  Action
                </Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {eips.map((eip) => (
              <CollapseTableRow
                key={eip.resID}
                colSpan={24}
                tableClassName="group !border-none ring-1 ring-neutral-200 rounded-xl"
                content={
                  <div className="mx-4 rounded-lg group">
                    <EIPDetail eip={eip} regionDisplayName={getRegionDisplayName(eip.domain)} />
                  </div>
                }
              >
                <td colSpan={3} className="t-sb">
                  {eip.resID}
                </td>
                <td colSpan={5} className="t-sb">
                  {eip.name || '-'}
                </td>
                <td colSpan={3} className="t-sb">
                  {getRegionDisplayName(eip.domain)}
                </td>
                <td colSpan={3} className="t-sb">
                  {!eip.bandwidth
                    ? '-'
                    : eip.bandwidth.bandwidthType === 0
                      ? t('bandwidthShared')
                      : formatBitsPerSec(eip.bandwidth.rateBitsPerSec)}
                </td>
                <td colSpan={3}>
                  <span className="text-active t-sb">{t('statusAvailable')}</span>
                </td>
                <td colSpan={3} className="t-sb text-right">
                  {formatPrice(Math.abs(eip.amount))}
                </td>
                <td colSpan={3}>
                  <span
                    className="text-highlight cursor-pointer t-sb"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteEIP(eip)
                    }}
                  >
                    <Trans ns="eip" i18nKey="action.release">
                      Release
                    </Trans>
                  </span>
                </td>
              </CollapseTableRow>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="mt-4 mb-2">
          <EmptyView
            title={
              isStockDepleted ? (
                <Trans ns="eip" i18nKey="stockDepletedTitle">
                  IP Address Stock Depleted
                </Trans>
              ) : undefined
            }
            content={
              isStockDepleted ? (
                <Trans ns="eip" i18nKey="stockDepletedContent">
                  Please try again later
                </Trans>
              ) : (
                <Trans ns="eip" i18nKey="emptyEIPTitle">
                  No Data
                </Trans>
              )
            }
          />
        </div>
      )}

      {total > 0 && !domainLoading && !natLoading && (
        <Pagination
          size={pageSize}
          total={total}
          current={page}
          className="justify-end mt-4"
          onChange={(p) => setPage(p)}
        />
      )}

      <Suspense>
        {showCreateModal && (
          <EIPCreateModal
            domainInfos={domainInfos}
            onSuccess={() => {
              setShowCreateModal(false)
              refetchNATs()
              refetchStock()
            }}
            onClose={() => {
              setShowCreateModal(false)
            }}
          />
        )}

        {editEIP && (
          <EIPEditBandwidthModal
            eip={editEIP}
            onSuccess={() => {
              setEditEIP(undefined)
              refetchNATs()
            }}
            onClose={() => setEditEIP(undefined)}
          />
        )}

        {deleteEIP && (
          <EIPDeleteModal
            eip={deleteEIP}
            onSuccess={() => {
              setDeleteEIP(undefined)
              refetchNATs()
              refetchStock()
            }}
            onClose={() => setDeleteEIP(undefined)}
          />
        )}
      </Suspense>
    </div>
  )
}
